import http from '@shared/http';
import {
  COSMOS_V2_URL, NEXUS_V1_URL, COSMOS_V3_URL
} from '@shared/const';
import type { ClientsListSearchQuery } from '@modules/clients/presentation/routes';
import { EmailChannelProviderType } from '@modules/opportunities/presentation/pages/CaseCreation/views/BankSubmission/SubmitApplications/components/DirectToBankSubmissionSlido/const';
import { ClientsListDTO } from './dto/clientsList.dto';
import { ClientDetailsDTO } from './dto/clientDetails.dto';
import { AddUpdateClientRequestDTO, AddUpdateClientResponseDTO } from './dto/addUpdateClient.dto';
import { ClientProposalDTO } from './dto/clientProposal.dto';
import { ProposalDuplicateDTO } from './dto/proposalDuplicate.dto';

export const clientsApi = {
  getClientsList: ({
    q, page, edr: endDateRange, sdr: startDateRange, order, sort, stage, limit = '20',
  }: ClientsListSearchQuery & { limit?: string }) => {
    const startDate = startDateRange ? new Date(startDateRange) : null;
    const endDate = endDateRange ? new Date(endDateRange) : null;

    const params = new URLSearchParams({
      ...(q && q.length > 0 ? { q } : {}),
      ...(page && page > 0 ? { page: page.toString() } : {}),
      ...(endDate && startDate ? {
        last_updated_at_gte: new Date(Date.UTC(
          startDate.getUTCFullYear(),
          startDate.getUTCMonth(),
          startDate.getUTCDate()
        )).toISOString(),
        last_updated_at_lte: new Date(Date.UTC(
          endDate.getUTCFullYear(),
          endDate.getUTCMonth(),
          endDate.getUTCDate(),
          23,
          59,
          59,
          999
        )).toISOString(),
      } : {}),
      ...(order && sort ? { order, sort } : {}),
      limit,
    });

    if (stage && stage.length > 0) {
      const stagesArr = stage?.split('|');
      stagesArr?.forEach((stg, idx) => params.append(`stage[${idx}]`, stg));
    }
    return http.get<ClientsListDTO>(`${NEXUS_V1_URL}/clients?${params}`);
  },
  getClientDetails: (id: string) =>
    http.get<ClientDetailsDTO>(`${NEXUS_V1_URL}/clients/${id}`),
  addClient: (body: AddUpdateClientRequestDTO) => http.post<AddUpdateClientResponseDTO>(`${NEXUS_V1_URL}/clients`, body),
  updateClient: (clientId: string, body: AddUpdateClientRequestDTO) =>
    http.update<any>(`${NEXUS_V1_URL}/clients/${clientId}`, body),
  getClientProposals: (id: string) =>
    http.get<ClientProposalDTO>(`${COSMOS_V3_URL}/proposals/client/${id}`),
  duplicateClientProposal: (id: number) =>
    http.post<ProposalDuplicateDTO>(`${COSMOS_V2_URL}/proposals/${id}/duplicate`, {}),
  getEmailAuthLink: (callbackUrl: string, accountExternalId: string) => {
    const params = `callback_url=${callbackUrl}&account_external_id=${accountExternalId}`;
    return http.get<{ url: string }>(
      `${NEXUS_V1_URL}/messaging/authorize?${params}`
    );
  },
  getEmailPreferences: (accountExternalId: string) =>
    http.get<{
      emailPreferences: {
        isVerified: boolean;
        isPreferred: boolean;
        emailProvider: EmailChannelProviderType;
      }[];
    }>(
      `${NEXUS_V1_URL}/messaging/email-preferences/accounts/${accountExternalId}`
    ),
  setEmailPreferences: (
    accountExternalId: string,
    preferred_channel: EmailChannelProviderType
  ) =>
    http.update(
      `${NEXUS_V1_URL}/messaging/email-preferences/accounts/${accountExternalId}`,
      { preferred_channel }
    ),
  getCompanyClientsList: (companyId: string, {
    q, page, edr: endDateRange, sdr: startDateRange, order, sort, stage, limit = '20',
  }: ClientsListSearchQuery & { limit?: string }) => {
    const startDate = startDateRange ? new Date(startDateRange) : null;
    const endDate = endDateRange ? new Date(endDateRange) : null;

    const params = new URLSearchParams({
      ...(q && q.length > 0 ? { q } : {}),
      ...(page && page > 0 ? { page: page.toString() } : {}),
      ...(endDate && startDate ? {
        last_updated_at_gte: new Date(Date.UTC(
          startDate.getUTCFullYear(),
          startDate.getUTCMonth(),
          startDate.getUTCDate()
        )).toISOString(),
        last_updated_at_lte: new Date(Date.UTC(
          endDate.getUTCFullYear(),
          endDate.getUTCMonth(),
          endDate.getUTCDate(),
          23,
          59,
          59,
          999
        )).toISOString(),
      } : {}),
      ...(order && sort ? { order, sort } : {}),
      limit,
    });

    if (stage && stage.length > 0) {
      const stagesArr = stage?.split('|');
      stagesArr?.forEach((stg, idx) => params.append(`stage[${idx}]`, stg));
    }
    return http.get<ClientsListDTO>(`${NEXUS_V1_URL}/companies/${companyId}/clients?${params}`);
  },
  getCompanyClientDetails: (companyId: string, id: string) =>
    http.get<ClientDetailsDTO>(`${NEXUS_V1_URL}/companies/${companyId}/clients/${id}`),
  addCompanyClient: (companyId: string, body: AddUpdateClientRequestDTO) =>
    http.post<AddUpdateClientResponseDTO>(`${NEXUS_V1_URL}/companies/${companyId}/clients`, body),
  updateCompanyClient: (companyId: string, clientId: string, body: AddUpdateClientRequestDTO) =>
    http.update<any>(`${NEXUS_V1_URL}/companies/${companyId}/clients/${clientId}`, body),
  getClientByMobile: (companyId: string, mobile: string) =>
    // eslint-disable-next-line max-len
    http.get<ClientDetailsDTO>(`${NEXUS_V1_URL}/companies/${companyId}/clients/search-by-phone?mobile=${encodeURIComponent(mobile)}`),
  associateCompanyClient: (companyId: string, clientId: string) =>
    http.post<AddUpdateClientResponseDTO>(`${NEXUS_V1_URL}/companies/${companyId}/clients/${clientId}/associate`, {}),
};
